import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import Checkbox from 'component/Layout/Form/Checkbox';

import classNames from 'classnames';

import { useAttributeListAtom } from 'hooks/form/filter';
import { useAccommodationMeta } from 'hooks/form/meta';
import { useChangedFilterList } from 'hooks/form/useChangedFilterList';
import { QaIdentifier } from 'hooks/useQa';

import { filterPredicateIsTruthy } from 'utility/Check/filterPredicateIsTruthy';
import { diffSets, toggleValueInSet } from 'utility/Toggle/toggleValueInSet';

import { Attribute } from 'proto/Accommodation/Attributes';

import styles from '../Filter.module.scss';
import Segment from '../Segment';
import {
    MetaValue,
    useDisabledStatus,
} from '../Segment/__hooks/useDisabledStatus';
import FilterLabel from '../Segment/FilterLabel';

import { attributeListMap } from './__config/mapping';

interface ScopedAttributeProps {
    attributeListArray: Attribute[];
    headlineKey: string;
    disableWhen?: MetaValue;
    qaIdentifier?: QaIdentifier;
    refKey?: string;
}

const ScopedAttribute = ({
    attributeListArray,
    headlineKey,
    disableWhen = MetaValue.NoResult,
    qaIdentifier,
    refKey,
}: ScopedAttributeProps) => {
    const { t } = useTranslation();
    const [accommodationMeta] = useAccommodationMeta();
    const [attributeList, setAttributeList] = useAttributeListAtom();
    const { onToggle, onClear, isFilterChanged } = useChangedFilterList();
    const getDisabledStatus = useDisabledStatus(disableWhen);

    const onSelect = useCallback(
        (selection: Attribute) => {
            onToggle(selection);
            setAttributeList((current) => toggleValueInSet(current, selection));
        },
        [onToggle, setAttributeList]
    );

    return (
        <Segment
            headline={{ text: t(headlineKey) }}
            onDelete={() => {
                onClear(() =>
                    attributeListArray.filter((value) =>
                        attributeList.has(value)
                    )
                );
                setAttributeList((current) =>
                    diffSets(current, new Set(attributeListArray))
                );
            }}
            withDeleteLink
            qaIdentifier={qaIdentifier}
            kiFilterGroupData={{
                refKey: refKey ?? '',
                label: t(headlineKey),
                multi: true,
            }}
            filterList={attributeListArray
                .filter(
                    (attribute) =>
                        accommodationMeta?.attributes[attribute]?.hasCount
                )
                .map((attribute) => {
                    const isChecked = attributeList.has(attribute);
                    const meta = accommodationMeta?.attributes?.[attribute];
                    const label = t(attributeListMap[attribute] ?? 'TRANS');
                    const isDisabled =
                        !isChecked && meta && getDisabledStatus(meta);

                    return {
                        content: (
                            <Checkbox
                                key={attribute}
                                input={{
                                    id: `attribute_${attribute}`,
                                    value: attribute,
                                    name: `attribute_${attribute}`,
                                }}
                                isChecked={isChecked}
                                isDisabled={isDisabled}
                                isOnlyDeselectable={
                                    isChecked &&
                                    meta?.hasCount &&
                                    meta.count === 0
                                }
                                onChange={() => onSelect(attribute)}
                                withUXSelection
                                customClass={{
                                    Wrapper: classNames({
                                        [styles.Changed]:
                                            isFilterChanged(attribute),
                                    }),
                                }}
                            >
                                <FilterLabel labelText={label} {...meta} />
                            </Checkbox>
                        ),
                        isSelected: isChecked,
                        filterValue: label,
                        kiFilterData: {
                            label,
                            isSelectable: !isDisabled,
                            urlKey: refKey ?? '',
                            value: attribute ?? '',
                            ...meta,
                        },
                    };
                })
                .filter(filterPredicateIsTruthy)}
        />
    );
};
export default ScopedAttribute;
